// Data layer for the scaling results. The Forecasting > Scaling Results tab reads each run's
// `{id}_scaling.json` from S3 (useScalingRuns); the standalone Scaling (Preview) page still
// renders a gitignored local fixture (scalingFixture.local.json) in the same shape.
// Exposes typed combos + cascading option helpers so the dropdowns are data-driven.

export const Outcome = {
  VOLUME: 'volume',
  REVENUE: 'revenue'
} as const;
export type OutcomeKey = (typeof Outcome)[keyof typeof Outcome];

export interface ScalingScatterPoint {
  spend: number;
  volume: number;
  /** Days of data in that week (1-7); a missing value means a full week. */
  days?: number;
}

/** The fitted curve: outcome = a × spend^b (econ's NLS fit). */
export interface ScalingCurveParams {
  a: number;
  b: number;
}

export interface ScalingCurvePoint {
  spend: number;
  fit: number;
  lwrConf: number;
  uprConf: number;
  lwrPred: number;
  uprPred: number;
  extrapolated: string;
}

export interface ScalingMetrics {
  correlation: number;
  r2: number;
  mae: number;
  wmape: number;
  weekTotal: number;
  firstDay: string;
  lastDay: string;
}

export interface ScalingOutcomeData {
  efficiencyMetric: string; // 'CPA' | 'ROAS'
  extrapPoint: number;
  /** `{id}_scaling.json` only: the curve's last spend point, i.e. the run's real max weekly spend. */
  spendCeiling?: number;
  /** Where spendCeiling came from: the request, or econ's 1.5x-highest-week fallback. */
  ceilingSource?: 'requested' | 'auto_1.5x';
  /** The fit's parameters, for exact marginal/break-even math. Absent on older runs. */
  params?: ScalingCurveParams;
  metrics: ScalingMetrics;
  scatter: ScalingScatterPoint[];
  curve: ScalingCurvePoint[];
}

export interface ScalingCombo {
  platform: string;
  tactic: string;
  kpi: string;
  outcomes: Partial<Record<OutcomeKey, ScalingOutcomeData>>;
}

export interface ScalingFixture {
  scalingId: string;
  channel: string;
  combos: ScalingCombo[];
}

/**
 * `{id}_scaling.json`, written per run by econ's scaling_prod.R to
 * output/{client}/ (scaling design doc 5.5): the fixture shape plus run
 * metadata. `combos` holds one entry in v1; pooled dimensions are "".
 */
export interface ScalingRunFile extends ScalingFixture {
  schema_version: number;
  dataQaId: string;
  client: string;
  /** UTC ISO-8601. */
  generatedAt: string;
  request: {
    channel: string;
    platform: string;
    tactic: string;
    kpi: string;
    grain: string;
    maxWeeklySpend: number;
  };
}

// The fixture is local-only and gitignored, so it may be absent on a fresh clone / CI. Load it
// optionally via import.meta.glob — an empty match is fine (the page shows an empty state) and the
// build never breaks on a missing file.
//
// `import.meta.glob` is a Vite compile-time macro that Vite statically transforms. It is NOT available
// in non-Vite bundlers — notably Cypress's iife spec bundle, where `import.meta` is empty, so
// `import.meta.glob(...)` throws `is not a function` at module load and crashes every spec that loads
// the app (e.g. navbar.cy.ts). Guard the call so those builds fall back to the empty fixture; Vite
// still statically transforms the literal call inside the try, so the real app is unaffected.
const EMPTY_FIXTURE: ScalingFixture = { scalingId: '', channel: '', combos: [] };
let fixtureModules: Record<string, unknown> = {};
try {
  fixtureModules = import.meta.glob('./scalingFixture.local.json', { eager: true });
} catch {
  // Non-Vite bundler (e.g. Cypress iife) — import.meta.glob is unavailable; use the empty fixture.
}
const loaded = Object.values(fixtureModules)[0] as { default?: unknown } | undefined;

/** The standalone Scaling (Preview) page's local fixture; the Forecasting
 *  Scaling Results tab reads real runs from S3 instead (useScalingRuns). */
export const SCALING_FIXTURE: ScalingFixture =
  (loaded?.default as ScalingFixture | undefined) ?? EMPTY_FIXTURE;

const distinct = (values: string[]): string[] => [...new Set(values)];

// Separator for the combined Channel selector value (a key, never shown: see comboOptionItems).
// Not "/": an unlabelled tactic is literally "N/A", which a slash would split in two.
export const COMBO_SEPARATOR = '|||';

// The helpers below are pure functions of one loaded run (a fixture or a
// `{id}_scaling.json`), so the same selectors serve both pages.

// Distinct channel/platform/tactic combinations present in the data (KPI is selected separately).
export const comboOptions = (run: ScalingFixture): string[] =>
  distinct(run.combos.map(c => [run.channel, c.platform, c.tactic].join(COMBO_SEPARATOR)));

export const splitComboValue = (value: string): { platform: string; tactic: string } => {
  const [, platform = '', tactic = ''] = value.split(COMBO_SEPARATOR);
  return { platform, tactic };
};

export const platformOptions = (run: ScalingFixture): string[] =>
  distinct(run.combos.map(c => c.platform));

export const tacticOptions = (run: ScalingFixture, platform: string): string[] =>
  distinct(run.combos.filter(c => c.platform === platform).map(c => c.tactic));

export const kpiOptions = (run: ScalingFixture, platform: string, tactic: string): string[] =>
  distinct(run.combos.filter(c => c.platform === platform && c.tactic === tactic).map(c => c.kpi));

export const findCombo = (
  run: ScalingFixture,
  platform: string,
  tactic: string,
  kpi: string
): ScalingCombo | undefined =>
  run.combos.find(c => c.platform === platform && c.tactic === tactic && c.kpi === kpi);
